/* Navbar, cart drawer and light/dark toggle.
   Moved verbatim out of sections/header.liquid so it is cached instead of inlined on every page. */
  class NavbarToggle {
    constructor() {
      this.cartTrigger = document.querySelector('.js-open-cart');
      this.cartContainer = null;
      this.hamburger = document.querySelector('.navbar__hamburger');
      this.drawer = document.querySelector('.navbar__drawer');
      this.overlay = document.querySelector('.navbar__drawer-overlay');
      this.subscribeForm = document.querySelector('#drawer-subscribe-form');
      this.logo = document.querySelector('.navbar__logo-image');
      this.isOpen = false;
      // Light theme = black logo on cream/white pages.
      this.lightWordmark = 'https://cdn.shopify.com/s/files/1/0748/4552/0110/files/logolarge.png?v=1778867599';
      this.lightSymbol = 'https://cdn.shopify.com/s/files/1/0748/4552/0110/files/logosmall.png?v=1778867600';
      // Dark theme uses the same wordmark asset, tinted in CSS on dark sections.
      this.darkWordmark = this.lightWordmark;
      this.darkSymbol = 'https://cdn.shopify.com/s/files/1/0748/4552/0110/files/logo_symbol_png_white.png?v=1768853345';
      this.isScrolled = false;
      this.isTransitioning = false;
      this.isHomepage = document.body.classList.contains('template-index');
      this.logoStateKey = '';
      this.bandOn = false;

      this.init();
    }

    init() {
      // Cart drawer trigger — bound via event delegation on the document so
      // it works for EVERY cart trigger on the page (not just the first), and
      // stays wired up regardless of the nav-menu guard below. Previously this
      // binding lived after the `return` guard, so on any layout missing the
      // hamburger/drawer/overlay the cart link fell through to its native
      // href and navigated to the /cart page instead of opening the overlay.
      document.addEventListener('click', (e) => {
        const trigger = e.target.closest('.js-open-cart');
        if (!trigger) return;
        this.openCartDrawer(e);
      });

      // On the standalone /cart page the overlay is rendered directly into
      // the DOM by sections/cart.liquid (not injected via fetch), so wire up
      // its event delegation here too — otherwise Remove and qty buttons on
      // /cart would have no handler.
      //
      // CRITICAL: this script runs synchronously the moment the parser hits
      // it, which happens BEFORE the rest of the body (including cart.liquid)
      // has been parsed. So at this point document.querySelector finds
      // nothing on /cart. We must defer the lookup until the DOM is ready.
      const bindExistingCartOverlay = async () => {
        const existingCartOverlay = document.querySelector('.cart-drawer-overlay');
        if (existingCartOverlay) {
          this.bindCartDrawerEvents(existingCartOverlay);
          await this.refreshCartDrawer(existingCartOverlay);
          await this.#syncArchiveDiscount(existingCartOverlay);
        }
      };
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', bindExistingCartOverlay, { once: true });
      } else {
        bindExistingCartOverlay();
      }

      if (!this.hamburger || !this.drawer || !this.overlay) return;

      this.hamburger.addEventListener('click', () => this.toggleMenu());
      this.overlay.addEventListener('click', () => this.toggleMenu());

      if (this.subscribeForm) {
        this.subscribeForm.addEventListener('submit', this.handleSubscribe.bind(this));
      }

      window.addEventListener('scroll', () => this.handleScroll(), { passive: true });
      // The page can open already scrolled (a reload, a link to a place on the page).
      this.updateBand();
      window.addEventListener('pageshow', () => this.updateBand());

      document.addEventListener('click', async (e) => {
        const complimentaryButton = e.target.closest('[data-avalon-complimentary-add]');
        if (complimentaryButton && !complimentaryButton.disabled) {
          e.preventDefault();
          await this.addComplimentaryMonth(complimentaryButton);
          return;
        }

        // Single-edition Avalon upsell: data-avalon-upsell-add + data-variant-id
        const avalonBtn = e.target.closest('[data-avalon-upsell-add]');
        if (avalonBtn && !avalonBtn.disabled) {
          e.preventDefault();
          const variantId = avalonBtn.getAttribute('data-variant-id');
          if (!variantId) return;

          // Optional selling plan — subscription upsells (e.g. The Avalon
          // Letters) must add their variant with a selling_plan id.
          const sellingPlan = avalonBtn.getAttribute('data-selling-plan');

          avalonBtn.disabled = true;
          const originalText = avalonBtn.textContent;
          avalonBtn.textContent = '...';

          const addPayload = { id: parseInt(variantId, 10), quantity: 1, properties: window.ViuletUpsell?.properties(avalonBtn.matches('.cart-drawer__stone-add') ? 'marble_base' : sellingPlan ? 'avalon_membership' : 'avalon_single_edition', avalonBtn) || {} };
          if (sellingPlan) addPayload.selling_plan = parseInt(sellingPlan, 10);

          try {
            const res = await fetch('/cart/add.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify(addPayload),
            });
            if (!res.ok) throw new Error('Failed to add');
            const overlay = avalonBtn.closest('.cart-drawer-overlay');
            if (overlay) {
              await this.refreshCartDrawer(overlay);
              await this.#syncArchiveDiscount(overlay);
            } else {
              window.location.reload();
            }
          } catch (err) {
            console.error('Avalon upsell add failed', err);
            avalonBtn.disabled = false;
            avalonBtn.textContent = originalText;
          }
          return;
        }

        // Multi-variant Avalon upsell: #AvalonBundleAdd reads any number
        // of data-variant-1..N attributes in order and adds them
        // sequentially, so the bundle scales as new editions are added
        // to the Avalon Letters collection without code changes.
        const multiBtn = e.target.closest('#AvalonBundleAdd, [data-avalon-bundle-add]');
        if (multiBtn && !multiBtn.disabled) {
          e.preventDefault();
          const variantIds = [];
          let idx = 1;
          while (true) {
            const v = multiBtn.getAttribute(`data-variant-${idx}`);
            if (!v) break;
            variantIds.push(v);
            idx++;
            if (idx > 50) break; // safety against runaway loop
          }
          if (variantIds.length === 0) return;

          const overlay = multiBtn.closest('.cart-drawer-overlay');
          multiBtn.disabled = true;
          const originalText = multiBtn.textContent;
          multiBtn.textContent = '...';

          try {
            for (const v of variantIds) {
              const res = await fetch('/cart/add.js', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ id: parseInt(v, 10), quantity: 1, properties: window.ViuletUpsell?.properties('avalon_bundle', multiBtn) || {} }),
              });
              if (!res.ok) throw new Error(`Failed to add variant ${v}`);
            }
            if (overlay) {
              await this.refreshCartDrawer(overlay);
              await this.#syncArchiveDiscount(overlay);
            } else {
              window.location.reload();
            }
          } catch (err) {
            console.error('Avalon bundle add failed', err);
            multiBtn.disabled = false;
            multiBtn.textContent = originalText;
          }
        }
      });
    }
    async openCartDrawer(e) {
      if (e) e.preventDefault();

      const existing = document.querySelector('.cart-drawer-overlay');
      if (existing) {
        // Reuse an existing overlay rather than refetching the cart section.
        // The section can be the standalone /cart page (always visible) or
        // a previously-injected drawer that we hid on close. Refetching it
        // every open hammers Shopify's section endpoint and triggers the
        // Cloudflare "verify your connection" challenge after a few cycles.
        const wasHidden = existing.style.display === 'none';
        existing.style.display = '';
        existing.classList.remove('is-closing');
        const innerDrawer = existing.querySelector('.cart-drawer');
        if (innerDrawer) innerDrawer.classList.remove('is-closing');
        document.body.classList.add('cart-drawer-open');

        // For drawer-mode overlays that we hid on close, re-trigger the
        // open animation so it slides in like a fresh open. Also lock the
        // background scroll. The standalone /cart page IS the page; we
        // never lock scroll or replay animations for it.
        if (wasHidden && existing.dataset.injected === 'true') {
          existing.style.animation = 'none';
          if (innerDrawer) innerDrawer.style.animation = 'none';
          // Force reflow so the next assignment restarts the animation.
          // eslint-disable-next-line no-unused-expressions
          existing.offsetHeight;
          existing.style.animation = '';
          if (innerDrawer) innerDrawer.style.animation = '';
          this.#lockBackgroundScroll();
        }
        await this.refreshCartDrawer(existing);
        return;
      }

      try {
        await this.#syncComplimentaryAvalon();
        const res = await fetch('/cart?sections=cart', {
          headers: { Accept: 'application/json' },
        });
        if (!res.ok) {
          console.error('Failed to load cart drawer', res.status);
          window.location.href = '/cart';
          return;
        }
        const json = await res.json();
        const html = json?.cart || '';
        const temp = document.createElement('div');
        temp.innerHTML = html;
        const drawerOverlay = temp.querySelector('.cart-drawer-overlay');
        if (!drawerOverlay) {
          window.location.href = '/cart';
          return;
        }
        // Mark this overlay as drawer-injected so refresh/close logic can
        // distinguish it from the standalone /cart page overlay (which
        // shouldn't be scroll-locked or hidden on close).
        drawerOverlay.dataset.injected = 'true';
        document.body.appendChild(drawerOverlay);
        document.body.classList.add('cart-drawer-open');
        this.#lockBackgroundScroll();

        // Close on overlay or close button. Replace the inline onclick
        // (which uses history.back() — appropriate on /cart but wrong here)
        // with a handler that hides the drawer.
        const closeBtn = drawerOverlay.querySelector('.cart-drawer__close');
        if (closeBtn) {
          closeBtn.removeAttribute('onclick');
          closeBtn.addEventListener('click', () => this.closeCartDrawer(drawerOverlay));
        }
        drawerOverlay.addEventListener('click', (evt) => {
          if (evt.target === drawerOverlay) this.closeCartDrawer(drawerOverlay);
        });

        this.bindCartDrawerEvents(drawerOverlay);
      } catch (err) {
        console.error('Failed to load cart drawer', err);
        window.location.href = '/cart';
      }
    }

    closeCartDrawer(overlay) {
      if (!overlay) overlay = document.querySelector('.cart-drawer-overlay');
      const drawer = overlay?.querySelector('.cart-drawer');
      if (!overlay || !drawer) return;

      drawer.classList.add('is-closing');
      overlay.classList.add('is-closing');
      document.body.classList.remove('cart-drawer-open');

      setTimeout(() => {
        // Hide rather than destroy the overlay. Keeping it in the DOM:
        //   - avoids a section refetch on the next open (the trigger for
        //     Cloudflare's bot-protection challenge)
        //   - preserves the delegated event listeners on the overlay
        //   - keeps the close-button binding intact across opens
        // Only applies to drawer-injected overlays; the standalone /cart
        // page closes via history.back() (its inline onclick handler) and
        // never reaches this branch.
        if (overlay.dataset.injected === 'true') {
          overlay.style.display = 'none';
        }
        overlay.classList.remove('is-closing');
        drawer.classList.remove('is-closing');

        this.#unlockBackgroundScroll();
      }, 300);
    }

    #lockBackgroundScroll() {
      const scrollY = window.scrollY || window.pageYOffset;
      document.body.dataset.scrollY = String(scrollY);
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.left = '0';
      document.body.style.right = '0';
      document.body.style.width = '100%';
      document.body.style.height = '100vh';
      document.body.style.height = '100svh';
    }

    #unlockBackgroundScroll() {
      const y = parseInt(document.body.dataset.scrollY || '0', 10) || 0;
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.left = '';
      document.body.style.right = '';
      document.body.style.width = '';
      document.body.style.height = '';
      window.scrollTo(0, y);
    }

    async addComplimentaryMonth(button) {
      if (this.complimentaryBusy) return;
      const offer = button.closest('[data-avalon-complimentary]');
      const overlay = button.closest('.cart-drawer-overlay');
      const status = offer.querySelector('[data-avalon-complimentary-status]');
      const root = window.Shopify?.routes?.root || '/';
      const code = 'AVALONCOMPLIMENTARY';
      const variantId = Number(offer.dataset.variantId);
      const planId = Number(offer.dataset.sellingPlan);
      const read = async () => {
        const response = await fetch(`${root}cart.js`, { cache: 'no-store' });
        if (!response.ok) throw new Error('cart');
        return response.json();
      };
      const post = async (path, data) => {
        const response = await fetch(`${root}cart/${path}.js`, {
          method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(data)
        });
        if (!response.ok) throw new Error('cart');
        return response.json();
      };
      const codesOf = cart => (cart.discount_codes || []).map(discount => discount.code).filter(Boolean);
      const isOurs = item => item.variant_id === variantId && item.properties?._avalon_complimentary === 'v1';
      const locks = [...overlay.querySelectorAll('button, input')].map(element => [element, element.disabled]);
      this.complimentaryBusy = true;
      overlay.setAttribute('aria-busy', 'true');
      locks.forEach(([element]) => { element.disabled = true; });
      status.textContent = offer.dataset.adding;
      let previous = null;
      let attemptedAdd = false;
      let recover = false;
      try {
        previous = await read();
        if (!previous.items.some(item => (item.handle.startsWith('avalon-letters-') || item.product_id === 9512416379118) && !item.selling_plan_allocation && item.final_line_price > 0 && !item.properties?._atelier_gift)) throw new Error('edition');
        if (previous.items.some(item => item.variant_id === variantId || item.handle === 'the-avalon-letters-yearly-subscription')) {
          await this.refreshCartDrawer(overlay);
          return;
        }
        attemptedAdd = true;
        await post('add', { items: [{ id: variantId, quantity: 1, selling_plan: planId, properties: { ...window.ViuletUpsell?.properties('avalon_complimentary_month', button), _avalon_complimentary: 'v1' } }] });
        const codes = [...new Set([...codesOf(previous), code])];
        await post('update', { discount: codes.join(',') });
        const updated = await read();
        const line = updated.items.find(isOurs);
        const priorPaid = previous.items.reduce((total, item) => total + item.final_line_price, 0);
        const nowPaid = updated.items.filter(item => !isOurs(item)).reduce((total, item) => total + item.final_line_price, 0);
        const codesPreserved = (previous.discount_codes || []).filter(item => item.applicable).every(item =>
          (updated.discount_codes || []).some(next => next.code.toUpperCase() === item.code.toUpperCase() && next.applicable));
        if (!line || line.quantity !== 1 || line.final_line_price !== 0 || line.selling_plan_allocation?.selling_plan?.id !== planId || !codesPreserved || nowPaid > priorPaid) throw new Error('offer');
        await this.refreshCartDrawer(overlay);
      } catch (error) {
        if (attemptedAdd) {
          try {
            const current = await read();
            for (const line of current.items.filter(isOurs)) await post('change', { id: line.key, quantity: 0 });
            await post('update', { discount: codesOf(previous).join(',') });
            const restored = await read();
            if (restored.items.some(isOurs)) throw new Error('restore');
          } catch (rollbackError) { recover = true; }
        }
        status.textContent = recover ? offer.dataset.recovery : offer.dataset.error;
      } finally {
        this.complimentaryBusy = false;
        overlay.removeAttribute('aria-busy');
        locks.forEach(([element, disabled]) => { element.disabled = disabled; });
        if (recover) overlay.querySelectorAll('.cart__checkout').forEach(element => { element.disabled = true; });
      }
    }

    bindCartDrawerEvents(overlay) {
      if (!overlay) return;
      // Guard against double-binding. The overlay element persists across
      // refreshes (we only swap .cart-content / .cart-drawer__sticky), and
      // event delegation already covers newly-inserted inner nodes — so
      // binding once is correct.
      if (overlay.dataset.cartEventsBound === '1') return;
      overlay.dataset.cartEventsBound = '1';

      overlay.addEventListener('click', async (e) => {
        const upsellForm = e.target.closest('.cart-upsell__form');
        const removeBtn = e.target.closest('.cart-item__remove');
        const btn = e.target.closest('.quantity__button');
        if (!btn && !upsellForm && !removeBtn) return;

        e.preventDefault();

        if (upsellForm) {
          const formData = new FormData(upsellForm);
          const id = formData.get('id');
          const quantity = parseInt(formData.get('quantity') || '1', 10) || 1;
          await this.#cartMutate(overlay, '/cart/add.js', { id, quantity, properties: { ...Object.fromEntries([...formData].filter(([key]) => /^properties\[.+\]$/.test(key)).map(([key, value]) => [key.slice(11, -1), value])), ...window.ViuletUpsell?.properties('marble_base', upsellForm) } });
          return;
        }

        if (removeBtn) {
          // Prefer the stable line-item key over the 1-based line index:
          // line indexes shift when items are added/removed and can race
          // with concurrent updates, removing the wrong line. item.key
          // identifies the exact line regardless of position.
          const cartItem = removeBtn.closest('.cart-item');
          const lineKey = cartItem?.dataset.lineKey || '';
          const lineIndex = parseInt(cartItem?.dataset.lineIndex || '0', 10);
          if (!cartItem || (!lineKey && !lineIndex)) {
            console.warn('Cart remove: missing line key/index on', cartItem);
            return;
          }

          // Immediate visual feedback so the click is never "silent"
          // even if the request takes a moment or fails.
          removeBtn.disabled = true;
          cartItem.classList.add('is-removing');

          const updates = this.#groupQuantityUpdates(cartItem, 0);
          const body = updates ? { updates } : lineKey
            ? { id: lineKey, quantity: 0 }
            : { line: lineIndex, quantity: 0 };
          const ok = await this.#cartMutate(overlay, updates ? '/cart/update.js' : '/cart/change.js', body);
          if (!ok) {
            removeBtn.disabled = false;
            cartItem.classList.remove('is-removing');
          }
          return;
        }

        const cartItem = btn.closest('.cart-item');
        const input = cartItem?.querySelector('.quantity__input');
        const lineKey = cartItem?.dataset.lineKey || '';
        const lineIndex = parseInt(cartItem?.dataset.lineIndex || '0', 10);
        if (!input || (!lineKey && !lineIndex)) return;

        const delta = btn.dataset.action === 'increase' ? 1 : -1;
        const current = parseInt(input.value || '0', 10);
        const next = Math.max(0, current + delta);

        const updates = this.#groupQuantityUpdates(cartItem, next);
        const body = updates ? { updates } : lineKey
          ? { id: lineKey, quantity: next }
          : { line: lineIndex, quantity: next };
        await this.#cartMutate(overlay, updates ? '/cart/update.js' : '/cart/change.js', body);
      });
    }

    #groupQuantityUpdates(cartItem, next) {
      const lines = JSON.parse(cartItem.dataset.lineGroup || '[]');
      if (lines.length < 2) return null;
      const updates = Object.fromEntries(lines.map(line => [line.key, line.quantity]));
      const total = lines.reduce((sum, line) => sum + line.quantity, 0);
      if (next >= total) updates[lines[0].key] += next - total;
      else {
        let remaining = total - next;
        for (const line of [...lines].reverse()) {
          const reduction = Math.min(remaining, line.quantity);
          updates[line.key] -= reduction;
          remaining -= reduction;
        }
      }
      return updates;
    }

    // Single round-trip cart mutation: POST to /cart/{add,change}.js with
    // sections=cart so the response includes both the cart JSON and the
    // re-rendered section HTML. This avoids a second fetch to
    // /cart?section_id=cart (which is the request that triggers
    // Shopify's Cloudflare bot challenge after a few cycles).
    async #cartMutate(overlay, endpoint, body) {
      if (this.complimentaryBusy || this.cartMutationBusy) return false;
      this.cartMutationBusy = true;
      try {
        const res = await fetch(endpoint, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: JSON.stringify({ ...body, sections: 'cart' }),
        });
        if (!res.ok) {
          const text = await res.text().catch(() => '');
          console.error(`Cart mutation ${endpoint} failed`, res.status, text);
          return false;
        }
        const json = await res.json();
        const sectionHtml = json?.sections?.cart;
        const giftChanged = await this.#syncComplimentaryAvalon();
        if (giftChanged) {
          await this.refreshCartDrawer(overlay);
        } else if (sectionHtml) {
          this.#updateCartFromSection(overlay, sectionHtml);
        } else {
          // Fallback path: API didn't return sections for some reason.
          // Use the GET sections endpoint, which is still cheaper than
          // the legacy ?section_id= HTML endpoint.
          await this.refreshCartDrawer(overlay);
        }
        await this.#syncArchiveDiscount(overlay);
        return true;
      } catch (err) {
        console.error(`Cart mutation ${endpoint} threw`, err);
        return false;
      } finally {
        this.cartMutationBusy = false;
      }
    }

    // Swap the parts of the cart drawer that depend on cart state. The
    // outer overlay + close button + scroll-lock state are preserved so
    // the user's open/closed state and bindings survive the refresh.
    #updateCartFromSection(overlay, sectionHtml) {
      const temp = document.createElement('div');
      temp.innerHTML = sectionHtml;

      // The cart-drawer modifier class (e.g. cart-drawer--avalon) is
      // conditional on which items are in the cart. Mirror it so the
      // Avalon-themed styles toggle correctly when the subscription is
      // added or removed.
      const newDrawer = temp.querySelector('.cart-drawer');
      const existingDrawer = overlay.querySelector('.cart-drawer');
      if (newDrawer && existingDrawer) {
        existingDrawer.className = newDrawer.className;
        for (const attr of newDrawer.attributes) {
          if (attr.name.startsWith('data-')) {
            existingDrawer.setAttribute(attr.name, attr.value);
          }
        }
      }

      const newContent = temp.querySelector('.cart-content');
      const existingContent = overlay.querySelector('.cart-content');
      if (newContent && existingContent) {
        existingContent.replaceWith(newContent);
      }

      const newSticky = temp.querySelector('.cart-drawer__sticky');
      const existingSticky = overlay.querySelector('.cart-drawer__sticky');
      if (existingSticky && newSticky) {
        existingSticky.replaceWith(newSticky);
      } else if (existingSticky && !newSticky) {
        // Cart became empty — sticky should disappear.
        existingSticky.remove();
      } else if (!existingSticky && newSticky && existingDrawer) {
        // Cart went from empty to non-empty — re-attach the sticky.
        existingDrawer.appendChild(newSticky);
      }
    }

    // Keep the gift consistent for every storefront entry point and existing carts.
    async #syncComplimentaryAvalon() {
      if (this.giftSync) return this.giftSync;
      this.giftSync = (async () => {
        const root = window.Shopify?.routes?.root || '/';
        const request = async (path, body) => {
          const response = await fetch(root + path, body ? {
            method: 'POST', headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(body)
          } : { cache: 'no-store' });
          if (!response.ok) throw new Error('Unable to update the atelier gift');
          return response.json();
        };
        let cart = await request('cart.js');
        let membershipChanged = false;
        const offeredMemberships = cart.items.filter(item => item.properties?._avalon_complimentary === 'v1' && item.handle === 'avalon');
        const hasPaidEdition = cart.items.some(item => (item.handle.startsWith('avalon-letters-') || item.product_id === 9512416379118) && !item.selling_plan_allocation && item.final_line_price > 0 && !item.properties?._atelier_gift);
        if (offeredMemberships.length && !hasPaidEdition) {
          for (const item of offeredMemberships) await request('cart/change.js', { id: item.key, quantity: 0 });
          const remainingCodes = (cart.discount_codes || []).map(item => item.code).filter(code => code.toUpperCase() !== 'AVALONCOMPLIMENTARY');
          await request('cart/update.js', { discount: remainingCodes.join(',') });
          membershipChanged = true;
          cart = await request('cart.js');
        }

        const gifts = [
          { productId: 9588431159534, variantId: 47858460164334, marker: 'girl-who-burned-mirror' }, // The Girl Who Burned, the Remaster (the original edition is sold out, 21 Oct 2026)
          { productId: 9512416379118, variantId: 47765950038254, marker: 'letters-remaster-mirror' },
          { productId: 9527903715566, variantId: 48357535875310, marker: 'strawberry-mirror' }
        ];
        const updates = {};
        const additions = [];
        for (const gift of gifts) {
          const eligible = cart.items.some(item => item.product_id === gift.productId && !item.selling_plan_allocation && item.final_line_price > 0);
          const editions = cart.items.filter(item => item.variant_id === gift.variantId && !item.selling_plan_allocation);
          if (eligible && !editions.length) {
            additions.push({ id: gift.variantId, quantity: 1, properties: { ...window.ViuletUpsell?.properties(gift.marker, null, 'automatic_gift', 'automatic'), _atelier_gift: gift.marker } });
          }
          let keptGift = false;
          for (const item of editions) {
            if (item.properties?._atelier_gift !== gift.marker) continue;
            if (!eligible || keptGift) updates[item.key] = 0;
            else {
              keptGift = true;
              if (item.quantity > 1) updates[item.key] = 1;
            }
          }
        }
        if (Object.keys(updates).length) await request('cart/update.js', { updates });
        if (additions.length) await request('cart/add.js', { items: additions });
        return !!(membershipChanged || Object.keys(updates).length || additions.length);
      })();
      try { return await this.giftSync; }
      finally { this.giftSync = null; }
    }

    // External refresh hook for callers that mutated the cart without going
    // through #cartMutate (e.g. add-to-cart from a product page). Uses the
    // JSON sections endpoint so the response shape is predictable and we
    // don't accidentally swallow a Cloudflare challenge page as "no
    // .cart-content" → window.location.reload() → verification loop.
    async refreshCartDrawer(overlay) {
      if (!overlay) overlay = document.querySelector('.cart-drawer-overlay');
      if (!overlay) return;
      try {
        await this.#syncComplimentaryAvalon();
        const res = await fetch('/?sections=cart', {
          headers: {
            Accept: 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
        });
        if (!res.ok) {
          console.error('refreshCartDrawer failed', res.status);
          return;
        }
        const json = await res.json();
        const sectionHtml = json?.cart;
        if (!sectionHtml) {
          console.warn('refreshCartDrawer: no cart section in response');
          return;
        }

        this.#updateCartFromSection(overlay, sectionHtml);

        // Only rebind the close button for drawer-mode overlays. On the
        // standalone /cart page the inline onclick="window.history.back()"
        // is the correct close behavior; replacing it with closeCartDrawer
        // would hide the overlay and leave the user staring at an empty
        // page.
        if (overlay.dataset.injected === 'true') {
          const closeBtn = overlay.querySelector('.cart-drawer__close');
          if (closeBtn && closeBtn.getAttribute('onclick')) {
            closeBtn.removeAttribute('onclick');
            closeBtn.addEventListener('click', () => this.closeCartDrawer(overlay));
          }
        }
        this.bindCartDrawerEvents(overlay);
      } catch (err) {
        console.error('refreshCartDrawer threw', err);
      }
    }

    #countArchiveEditions(cart, editionIds) {
      const wanted = new Set(editionIds);
      const found = new Set();
      for (const item of cart.items || []) {
        const id = String(item.product_id);
        if (wanted.has(id)) found.add(id);
      }
      return found.size;
    }

    #archiveCodeIsApplied(cart, code) {
      const want = code.toUpperCase();
      return (cart.discount_codes || []).some((entry) => (entry.code || '').toUpperCase() === want);
    }

    async #syncArchiveDiscount(overlay) {
      if (this.archiveDiscountSyncing) return;
      const drawer = (overlay || document).querySelector('.cart-drawer');
      if (!drawer) return;

      const code = (drawer.dataset.archiveDiscountCode || '').trim();
      if (!code) return;

      const editionIds = (drawer.dataset.archiveEditionIds || '')
        .split(',')
        .map((id) => id.trim())
        .filter(Boolean);
      if (editionIds.length < 7) return;

      this.archiveDiscountSyncing = true;
      try {
        const cartRes = await fetch('/cart.js', { credentials: 'same-origin' });
        if (!cartRes.ok) return;
        const cart = await cartRes.json();
        const count = this.#countArchiveEditions(cart, editionIds);
        const applied = this.#archiveCodeIsApplied(cart, code);

        let nextDiscount = null;
        if (count >= 7 && !applied) {
          nextDiscount = code;
        } else if (count < 7 && applied) {
          nextDiscount = '';
        }
        if (nextDiscount === null) return;

        const updateRes = await fetch('/cart/update.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: JSON.stringify({ discount: nextDiscount, sections: 'cart' }),
        });
        if (!updateRes.ok) {
          if (nextDiscount) {
            window.location.href = `/discount/${encodeURIComponent(nextDiscount)}?redirect=/cart`;
          }
          return;
        }

        const json = await updateRes.json();
        const sectionHtml = json?.sections?.cart;
        if (sectionHtml && overlay) {
          this.#updateCartFromSection(overlay, sectionHtml);
        } else if (overlay) {
          await this.refreshCartDrawer(overlay);
        }
      } catch (err) {
        console.error('Archive discount sync failed', err);
      } finally {
        this.archiveDiscountSyncing = false;
      }
    }

    // A soft band behind the header once the page has scrolled, set in
    // sections/header.liquid on `body.nav-scrolled`. Same moment as the logo
    // turning into the symbol. The home page has its own band, past its hero.
    updateBand() {
      if (this.isHomepage) return;
      // While the bag is open the page is held still and reports 0; its real place is kept on <body>.
      const held = document.body.style.position === 'fixed';
      const y = held
        ? parseInt(document.body.dataset.scrollY || '0', 10) || 0
        : window.scrollY || window.pageYOffset || 0;
      const scrolled = y > 100;
      if (scrolled !== this.bandOn) {
        this.bandOn = scrolled;
        document.body.classList.toggle('nav-scrolled', scrolled);
      }
    }

    async handleScroll() {
      this.updateBand();
      if (!this.logo || this.isTransitioning) return;

      const pastHero = document.body.classList.contains('home-temple-past-hero');
      const drawerOpen = document.body.classList.contains('drawer-open');
      const shouldBeScrolled = this.isHomepage ? pastHero : window.scrollY > 100;

      // Dark theme only over the homepage hero AND when the drawer isn't
      // open (the open drawer puts a cream surface behind the navbar, so we
      // switch to the black logo even on the homepage).
      const isDarkTheme = this.isHomepage && !pastHero && !drawerOpen;
      const wordmark = isDarkTheme ? this.darkWordmark : this.lightWordmark;
      const symbol = isDarkTheme ? this.darkSymbol : this.lightSymbol;
      const nextSrc = shouldBeScrolled ? symbol : wordmark;

      const logoKind = shouldBeScrolled ? 'symbol' : 'wordmark';
      const themeKey = `${isDarkTheme ? 'dark' : 'light'}-${logoKind}`;
      const themeChanged = themeKey !== this.logoStateKey;
      if (shouldBeScrolled !== this.isScrolled || themeChanged) {
        this.logoStateKey = themeKey;
        this.isScrolled = shouldBeScrolled;

        if (this.logo.src === nextSrc) return;

        this.isTransitioning = true;
        this.logo.classList.add('is-switching');

        await new Promise((resolve) => setTimeout(resolve, 250));

        await new Promise((resolve) => {
          const finish = () => {
            this.logo.onload = null;
            this.logo.onerror = null;
            resolve();
          };

          this.logo.onload = finish;
          this.logo.onerror = finish;
          this.logo.src = nextSrc;
          if (this.logo.complete) finish();
        });

        this.logo.classList.remove('is-switching');

        setTimeout(() => {
          this.isTransitioning = false;
        }, 250);
      }
    }

    handleSubscribe(e) {
      e.preventDefault();
      const form = e.target;
      const formContent = form.querySelector('.navbar__drawer-form-content');
      const successMessage = form.querySelector('.navbar__drawer-success');
      const email = form.querySelector('input[name="email_address"]').value;

      fetch(form.action, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          api_key: form.querySelector('input[name="api_key"]').value,
          email: email,
        }),
      })
        .then((response) => response.json())
        .then((data) => {
          formContent.style.display = 'none';
          successMessage.style.display = 'block';
          successMessage.classList.add('is-visible');
        })
        .catch((error) => {
          console.error('Error:', error);
        });
    }

    toggleMenu() {
      this.isOpen = !this.isOpen;

      if (this.isOpen) {
        this.drawer.classList.add('is-active');
        this.hamburger.classList.add('is-active');
        document.body.classList.add('drawer-open');
        document.body.style.overflow = 'hidden';
      } else {
        this.drawer.classList.remove('is-active');
        this.hamburger.classList.remove('is-active');
        document.body.classList.remove('drawer-open');
        document.body.style.overflow = '';
      }
      // Tell screen readers whether the menu is open.
      this.hamburger.setAttribute('aria-expanded', this.isOpen ? 'true' : 'false');

      // Re-evaluate the logo / theme because the drawer changes what's
      // visually behind the navbar.
      this.handleScroll();
    }
  }

  // Initialize navbar toggle and expose globally for programmatic use
  window.navbarToggle = new NavbarToggle();

  // ----------------------------------------------------------------
  // Light / dark theme toggle. The current theme is applied to
  // <html data-theme> before paint by the inline script in the layout
  // head; this module just keeps the button state in sync and persists
  // the choice. Default is dark.
  // ----------------------------------------------------------------
  class ThemeToggle {
    constructor() {
      this.storageKey = 'mbv-theme';
      this.buttons = document.querySelectorAll('.js-theme-toggle');
      if (!this.buttons.length) return;

      this.syncButtons(this.currentTheme());

      for (const button of this.buttons) {
        button.addEventListener('click', () => this.toggle());
      }
    }

    currentTheme() {
      return document.documentElement.dataset.theme === 'light' ? 'light' : 'dark';
    }

    toggle() {
      const next = this.currentTheme() === 'dark' ? 'light' : 'dark';
      document.documentElement.dataset.theme = next;
      try {
        localStorage.setItem(this.storageKey, next);
      } catch (e) {
        /* storage unavailable — theme still applies for this session */
      }
      this.syncButtons(next);
      document.dispatchEvent(new CustomEvent('themechange', { detail: { theme: next } }));
    }

    syncButtons(theme) {
      const isLight = theme === 'light';
      for (const button of this.buttons) {
        button.setAttribute('aria-checked', isLight ? 'true' : 'false');
      }
    }
  }

  window.themeToggle = new ThemeToggle();
